'use client';

import React, { useEffect, useState } from 'react';
import { Toaster as ReactToaster, ToastBar, toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ToastMessage from './components/toast-message';

// Fallback offset (desktop, announcement bar hidden) used until the real header
// height is measured. Toasts only appear on user action — well after mount — so
// this fallback is effectively never rendered; it just avoids a first-paint flash
// at react-hot-toast's default 16px top.
export const FALLBACK_TOP = 120;

const Toaster = () => {
  // EWM-600 — offset the toast below the fixed header so it never obstructs the
  // header icons (e.g. Cart). The header height varies (the black announcement
  // bar adds 40px, and it can be hidden per-page), and this global <Toaster>
  // doesn't know the header variant. A static worst-case offset (the original
  // fix) left the toast floating ~40px below the header whenever the bar was
  // hidden (EWM-600 follow-up, Nikki 2026-07-03). Instead we measure the real
  // header (#header-container, set in lib/tastics/header) at runtime and track
  // it with a ResizeObserver, so the toast always hugs the actual header bottom
  // — announcement bar shown or hidden, mobile or desktop.
  const [headerHeight, setHeaderHeight] = useState(FALLBACK_TOP);

  useEffect(() => {
    // Assumes the header is already mounted when this global <Toaster> mounts —
    // true today (both live in the layout shell). If it's absent we keep the
    // fallback; toasts only fire on user action, long after the shell hydrates.
    const header = document.getElementById('header-container');
    if (!header) return;

    // offsetHeight (not getBoundingClientRect) so the mobile hide-on-scroll
    // transform doesn't fold the toast up with the header — it stays anchored a
    // header's-height from the top.
    const update = () => setHeaderHeight(header.offsetHeight);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    // Horizontal: line the toast's right edge up with the cart icon's right edge.
    // The header main bar is full-width with `px-4 lg:px-8` (see jaycar-header/
    // components/main-bar) and the cart icon is its right-most child, so its right
    // edge sits at that padding. We mirror it as the container's right inset
    // (`!right-4 lg:!right-8`), overriding react-hot-toast's default 16px inset.
    // Vertical: `top` comes from the measured header height above (via
    // containerStyle so it overrides react-hot-toast's inline default).
    <ReactToaster position="top-right" containerClassName="!right-4 lg:!right-8" containerStyle={{ top: headerHeight }}>
      {(t) => (
        <div className="w-full md:w-[400px]">
          <ToastBar
            toast={t}
            style={{
              width: '100%',
              height: '100%',
              padding: '12px',
              margin: '0px auto',
              boxShadow: '-1px 8px 24px 0px rgba(0, 0, 0, 0.15), 0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
            }}
          >
            {({ icon, message }) => (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="block shrink-0">{icon}</span>
                  {message && <ToastMessage>{message}</ToastMessage>}
                </div>
                <XMarkIcon
                  data-testid="toast-dismiss"
                  className="size-5 shrink-0 hover:cursor-pointer"
                  onClick={() => toast.dismiss(t.id)}
                />
              </div>
            )}
          </ToastBar>
        </div>
      )}
    </ReactToaster>
  );
};

export default Toaster;
